import { Controller } from "@hotwired/stimulus"

// Phase 2.8 Tier 4 Step 40 — AI Conversational Report Builder modal.
// Two phases share the same controller scope:
//   1. compose: textarea + 10 example prompts + live cost preview +
//      "Generate" button.
//   2. suggestion: a single accept/edit/dismiss "inbox" rendered as
//      chips for dataset, columns, filters, group-by, and chart blocks.
//      Each chip removable; user accepts to open the builder pre-filled.
//
// Output is *never* auto-saved (Resolved Decision #13). Accept persists
// the DSL into the session, then the modal redirects to /reports/new
// where the standard ReportsController#create pipeline runs the
// validations + writes the history log + Mixpanel.
export default class extends Controller {
  static targets = [
    "composeStep", "suggestionStep",
    "question", "charsLabel", "tokenLabel", "costLabel",
    "rateLabel", "truncationWarning",
    "generateButton", "generateLabel",
    "costFinal", "tokensFinal",
    "suggestionBox", "nameInput",
    "datasetChip", "datasetLabel", "datasetKey",
    "columnChips", "filterChips", "filterLogicLabel", "filtersEmpty",
    "groupChips", "groupEmpty", "chartChips", "chartsEmpty",
    "validationBox", "validationList"
  ]

  static values = {
    previewUrl: String,
    generateUrl: String,
    acceptUrl: String,
    dismissUrl: String
  }

  connect() {
    this._previewTimer = null
    this._csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
    this._suggestion = null
    this._dataset = null
    this.previewDebounced()
  }

  fillExample(event) {
    const text = event.currentTarget.dataset.example || ""
    this.questionTarget.value = text
    this.questionTarget.focus()
    this.previewDebounced()
  }

  previewDebounced() {
    clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this.preview(), 250)
  }

  async preview() {
    const question = this.questionTarget.value
    this.charsLabelTarget.textContent = question.length.toLocaleString()
    const longEnough = question.trim().length >= 5
    this.generateButtonTarget.disabled = !longEnough
    if (!longEnough) {
      this.tokenLabelTarget.textContent = "0"
      this.costLabelTarget.textContent = "$0.0000"
      this.truncationWarningTarget.classList.add("hidden")
      return
    }

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ question }),
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this.tokenLabelTarget.textContent = data.estimated_tokens.toLocaleString()
      this.costLabelTarget.textContent = `$${data.estimated_cost_usd.toFixed(4)}`
      this.truncationWarningTarget.classList.toggle("hidden", !data.question_truncated)
      const rl = data.rate_limit
      this.rateLabelTarget.textContent = `${rl.remaining} of ${rl.limit} drafts remaining this hour`
      this.generateButtonTarget.disabled = !rl.allowed
    } catch (err) {
      console.warn("[report-ai-draft] preview failed", err)
    }
  }

  async generate() {
    const question = this.questionTarget.value
    if (question.trim().length < 5) return

    this.generateButtonTarget.disabled = true
    this.generateLabelTarget.textContent = "Generating…"

    try {
      const res = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ question }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._showToast({ message: data.error || `Generate failed (${res.status})`, type: "error" })
        return
      }

      this._renderSuggestion(data)
    } catch (err) {
      console.error("[report-ai-draft] generate failed", err)
      this._showToast({ message: "Generate failed — try again.", type: "error" })
    } finally {
      this.generateLabelTarget.textContent = "Generate"
      this.generateButtonTarget.disabled = false
    }
  }

  backToCompose() {
    this.suggestionStepTarget.classList.add("hidden")
    this.composeStepTarget.classList.remove("hidden")
  }

  _renderSuggestion(data) {
    this._suggestion = JSON.parse(JSON.stringify(data.suggestion))
    this._dataset = data.dataset
    this._acceptUrl = data.accept_url || this.acceptUrlValue
    this._totalTokenBucket = data.total_token_bucket
    this._costUsd = data.cost_usd
    this.costFinalTarget.textContent = `$${(data.cost_usd || 0).toFixed(4)}`
    this.tokensFinalTarget.textContent = `${data.total_token_bucket} tokens`

    this.nameInputTarget.value = this._suggestion.name || ""
    this.datasetLabelTarget.textContent = this._dataset?.label || this._suggestion.dataset_key
    this.datasetKeyTarget.textContent = this._suggestion.dataset_key

    this._renderColumnChips()
    this._renderFilterChips()
    this._renderGroupChips()
    this._renderChartChips()
    this._renderFilterLogic()
    this._renderValidation(data.validation_errors)

    this.composeStepTarget.classList.add("hidden")
    this.suggestionStepTarget.classList.remove("hidden")
  }

  _renderColumnChips() {
    const target = this.columnChipsTarget
    target.innerHTML = ""
    this._suggestion.column_keys.forEach((key, idx) => {
      const labelText = this._columnLabelFor(key)
      const chip = this._buildChip(labelText, "blue", () => {
        this._suggestion.column_keys.splice(idx, 1)
        this._renderColumnChips()
      })
      target.appendChild(chip)
    })
    if (this._suggestion.column_keys.length === 0) {
      target.appendChild(this._buildEmptyState("No columns"))
    }
  }

  _renderFilterChips() {
    const target = this.filterChipsTarget
    target.innerHTML = ""
    const filters = this._suggestion.filters || []
    if (filters.length === 0) {
      this.filtersEmptyTarget.classList.remove("hidden")
    } else {
      this.filtersEmptyTarget.classList.add("hidden")
    }
    filters.forEach((f, idx) => {
      const labelText = `${this._columnLabelFor(f.column_key)} ${f.operator}${f.value ? ` ${f.value}` : ""}`
      const chip = this._buildChip(labelText, "amber", () => {
        this._suggestion.filters.splice(idx, 1)
        this._renderFilterChips()
        this._renderFilterLogic()
      })
      target.appendChild(chip)
    })
  }

  _renderFilterLogic() {
    const logic = (this._suggestion.filter_logic || "").trim()
    this.filterLogicLabelTarget.textContent = logic ? `logic: ${logic}` : "match all (AND)"
  }

  _renderGroupChips() {
    const target = this.groupChipsTarget
    target.innerHTML = ""
    const groups = this._suggestion.group_by_keys || []
    if (groups.length === 0) {
      this.groupEmptyTarget.classList.remove("hidden")
    } else {
      this.groupEmptyTarget.classList.add("hidden")
    }
    groups.forEach((key, idx) => {
      const chip = this._buildChip(this._columnLabelFor(key), "emerald", () => {
        this._suggestion.group_by_keys.splice(idx, 1)
        this._renderGroupChips()
      })
      target.appendChild(chip)
    })
  }

  _renderChartChips() {
    const target = this.chartChipsTarget
    target.innerHTML = ""
    const charts = this._suggestion.chart_blocks || []
    if (charts.length === 0) {
      this.chartsEmptyTarget.classList.remove("hidden")
    } else {
      this.chartsEmptyTarget.classList.add("hidden")
    }
    charts.forEach((c, idx) => {
      const labelText = `${c.kind} by ${this._columnLabelFor(c.group_by_key)}${c.value_aggregate ? ` (${c.value_aggregate})` : ""}`
      const chip = this._buildChip(labelText, "indigo", () => {
        this._suggestion.chart_blocks.splice(idx, 1)
        this._renderChartChips()
      })
      target.appendChild(chip)
    })
  }

  _renderValidation(errors) {
    if (!errors || errors.length === 0) {
      this.validationBoxTarget.classList.add("hidden")
      return
    }
    this.validationBoxTarget.classList.remove("hidden")
    this.validationListTarget.innerHTML = ""
    errors.forEach((e) => {
      const li = document.createElement("li")
      li.textContent = e
      this.validationListTarget.appendChild(li)
    })
  }

  _columnLabelFor(key) {
    if (!this._dataset) return key
    const col = (this._dataset.columns || []).find(c => c.key === key)
    if (col) return col.label
    const filt = (this._dataset.filters || []).find(f => f.key === key)
    if (filt) return filt.label
    return key
  }

  _buildChip(text, color, onRemove) {
    const palettes = {
      blue:    ["bg-blue-50", "text-blue-800", "border-blue-200"],
      amber:   ["bg-amber-50", "text-amber-800", "border-amber-200"],
      emerald: ["bg-emerald-50", "text-emerald-800", "border-emerald-200"],
      indigo:  ["bg-indigo-50", "text-indigo-800", "border-indigo-200"],
      violet:  ["bg-violet-50", "text-violet-800", "border-violet-200"]
    }
    const classes = palettes[color] || palettes.blue
    const span = document.createElement("span")
    span.className = `inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] border ${classes.join(" ")}`
    const label = document.createElement("span")
    label.textContent = text
    span.appendChild(label)
    if (onRemove) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-current hover:opacity-70 cursor-pointer text-[14px] leading-none"
      btn.textContent = "×"
      btn.addEventListener("click", onRemove)
      span.appendChild(btn)
    }
    return span
  }

  _buildEmptyState(text) {
    const span = document.createElement("span")
    span.className = "text-[11px] text-gray-400 italic"
    span.textContent = text
    return span
  }

  async accept() {
    if (!this._suggestion) return
    this._suggestion.name = this.nameInputTarget.value.trim()

    try {
      const res = await fetch(this._acceptUrl, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          edited: true,
          suggestion: this._suggestion,
          total_token_bucket: this._totalTokenBucket,
          cost_usd: this._costUsd
        }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._showToast({ message: data.error || "Could not accept suggestion.", type: "error" })
        return
      }
      window.location = data.redirect_to
    } catch (err) {
      console.error("[report-ai-draft] accept failed", err)
      this._showToast({ message: "Accept failed — try again.", type: "error" })
    }
  }

  async dismiss() {
    try {
      await fetch(this.dismissUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ dataset_key: this._suggestion?.dataset_key }),
        credentials: "same-origin"
      })
    } catch (err) {
      console.warn("[report-ai-draft] dismiss tracking failed", err)
    }
    // Close the parent modal — same signal the candidate inbox controller uses.
    const modal = this.element.closest("[data-controller~='modal']")
    modal?.querySelector("[data-action*='modal#close']")?.click()
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this._csrf,
      "X-Requested-With": "XMLHttpRequest"
    }
  }

  _showToast({ message, type = "success" }) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
